import { MergedStation } from './../model/merged-station.interface';
import { StationService } from './../../core/services/station.service';
import { chain } from 'lodash';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { take } from 'rxjs/operators/take';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { Subject } from 'rxjs/Subject';
import { MarketFormatPipe } from '../market-format.pipe';
import { StationUtilsService } from '../utils/station-utils.service';
import { V } from '@angular/cdk/keycodes';

export interface SingleDropboxData {
  id: any;
  name: string;
  model?: any;
  mergedStation?: MergedStation;
}

@Component({
  selector: 'app-single-dropbox',
  templateUrl: './single-dropbox.component.html',
  styleUrls: ['./single-dropbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleDropboxComponent),
      multi: true
    },
    MarketFormatPipe
  ]
})
export class SingleDropboxComponent
  implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {
  public value = null;

  public filterControl;

  @Input()
  public placeholder: string;

  @Input()
  public isDisabled = false;

  @Input()
  public allowMultiple = true;

  @Input()
  public hideFilterButton = false;

  @Input()
  public adjustPopupPosition = true;

  @Input()
  public popupPositionLeft: 40;

  @Input()
  public placeholderLabel: string;

  @Input()
  public noEntriesFoundLabel: string;

  @Input()
  public showStationBadge = false;

  @Input()
  public cssClassToApply = 'dropbox multi-dropbox-custom';

  @Input()
  public iconClass = 'material-icons dropbox-search';

  @Output()
  public modelChange: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public selectionChanged: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  public editIconClicked: EventEmitter<SingleDropboxData> = new EventEmitter<SingleDropboxData>();

  private allItems: SingleDropboxData[];

  public filteredItems: ReplaySubject<SingleDropboxData[]>;

  @ViewChild(MatSelect, { static: true }) select: MatSelect;

  private _onDestroy = new Subject<void>();

  hideSelectAll = false;

  hideSelectNone = true;

  constructor(
    private eleRef: ElementRef,
    private stationUtilsService: StationUtilsService,
    private stationService: StationService) {
    this.filterControl = new FormControl('');
    this.filteredItems = new ReplaySubject<SingleDropboxData[]>();
  }

  ngOnInit() {
    this.filterControl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterItems();
        this.hideSelectAll = false;
        this.hideSelectNone = true;
      });
  }

  ngAfterViewInit() {
    this.setInitialValue();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  public setList(list: SingleDropboxData[]) {
    if (this.showStationBadge) {
      list = this.groupByValue(list);
    }
    this.allItems = list;
    this.filteredItems.next(this.allItems);
    this.hideSelectAll = false;
    this.hideSelectNone = true;
  }

  /**
   * Verify if the option value id is equal to or contains the id from the selection
   * @param valueFromOptions a value from options
   * @param valueFromSelection a selected value
   * @returns true if value from selection was found in the options
   */
  private compareDropboxData(
    valueFromOptions: SingleDropboxData,
    valueFromSelection: SingleDropboxData) {

    // If one of values does not exist
    if (!valueFromOptions || !valueFromSelection) {
      return false;
    }
    // If both id properties are array
    else if (Array.isArray(valueFromOptions.id) && Array.isArray(valueFromSelection.id)) {
      return valueFromOptions.id.some((id: string) => valueFromSelection.id.includes(id));
    }
    // If only the id property from options is an array
    else if (Array.isArray(valueFromOptions.id)) {
      return valueFromOptions.id.some((id: string) => id === valueFromSelection.id);
    }
    // If only the id property from selection is an array
    else if (Array.isArray(valueFromSelection.id)) {
      return valueFromSelection.id.some((id: string) => id === valueFromOptions.id);
    // If both id properties are not array
    } else {
      return valueFromOptions.id === valueFromSelection.id;
    }
  }

  private setInitialValue() {
    this.filteredItems
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(() => {
        // setting the compareWith property to a comparison function
        // triggers initializing the selection according to the initial value of
        // the form control (i.e. _initializeSelection())
        // this needs to be done after the filteredItems are loaded initially
        // and after the mat-option elements are available
        this.select.compareWith = this.compareDropboxData;
      });
  }

  private filterItems() {
    if (!this.allItems) {
      return;
    }
    // get the search keyword
    let search = this.filterControl.value;
    if (!search || search.length === 0) {
      this.filteredItems.next(this.allItems);
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the items
    this.filteredItems.next(
      this.allItems.filter((item) => {
        return item.name.toLowerCase().indexOf(search) > -1;
      })
    );
  }

  /**
   * Invoked when the model has been changed
   */
  onChange: (_: any) => void = (_: any) => { };

  /**
   * Invoked when the model has been touched
   */
  onTouched: () => void = () => { };

  /**
   * Method that is invoked on an update of a model.
   */
  updateChanges() {
    this.onChange(this.value);
    this.modelChange.emit(this.value);
  }

  /**
   * Writes a new item to the element.
   * @param value the value
   */
  writeValue(value): void {
    if (this.value !== value) {
      this.value = value;
    }
  }

  /**
   * Registers a callback function that should be called when the control's value changes in the UI.
   * @param fn
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers a callback function that should be called when the control receives a blur event.
   * @param fn
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onOpenedChange(e: boolean): void {
    if (e && this.adjustPopupPosition) {
      const elements: HTMLElement[] = document.getElementsByClassName(
        'cdk-overlay-pane-select-search'
      ) as any as HTMLElement[];
      for (let i = 0; i < elements.length; i++) {
        const ele: HTMLElement = elements[i];
        const top = parseInt(ele.style.top, 0);
        const left = parseInt(ele.style.left, 0);
        ele.style.top = top + 75 + 'px';
        ele.style.left = left + Number(this.popupPositionLeft) + 'px';
      }
    }
  }

  onFilter() {
    this.select.close();
    this.updateChanges();
  }

  applySelectionChanges(e?: any) {
    this.selectionChanged.emit(this.value);
    this.updateChanges();
  }

  editClicked(item: SingleDropboxData) {
    this.editIconClicked.emit(item);
  }

  selectAll() {
    const search = this.filterControl.value || '';
    this.value = this.allItems.filter((item) => {
      return item.name.toLowerCase().indexOf(search.toLowerCase()) > -1;
    });
    this.applySelectionChanges();
    this.hideSelectAll = true;
    this.hideSelectNone = false;
  }

  selectNone() {
    this.value = [];
    this.applySelectionChanges();
    this.hideSelectAll = false;
    this.hideSelectNone = true;
  }

  get isAllItemsSelected() {
    return true;
  }

  getAllItems() {
    return this.allItems;
  }

  private groupByValue(list: SingleDropboxData[]):SingleDropboxData[]{
    return (
      chain(list)
        .groupBy('model.name')
        .map((value: SingleDropboxData[], key: string) => {
          const mergedStation = this.stationService.groupStationByName(value.map((v) => v.model))[0];
          return {
            id: value.map((v) => v.id),
            name: mergedStation.name,
            model: value.map((v) => v.model),
            mergedStation: mergedStation,
          };
        })
        .value()
    );
  }
}
